import { useEffect, useMemo, useState } from "react";

import { ApiSuffixType, CrudApi, ICourseDto, Log } from "@ryde/shared";

/**
 * Loads course names/metadata for schedules preview in one list request.
 * Course has no /search $in endpoint; we fetch the company course list once
 * (same as the courses grid) and keep only requested ids. Missing/deleted
 * courses are simply omitted — rows still render with #id.
 */
export const useCourseSchedulesPreviewCourses = (
  courseIds: number[],
): {
  coursesById: Record<number, ICourseDto>;
  isLoadingCourses: boolean;
} => {
  const [coursesById, setCoursesById] = useState<Record<number, ICourseDto>>(
    {},
  );
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);

  const uniqueCourseIds = useMemo(
    () => [...new Set(courseIds.filter(id => id > 0))],
    [courseIds],
  );
  const courseIdsKey = uniqueCourseIds.join(",");

  useEffect(() => {
    let isCancelled = false;

    const loadCourses = async () => {
      if (!uniqueCourseIds.length) {
        setCoursesById({});
        return;
      }

      setIsLoadingCourses(true);
      try {
        const courses = await CrudApi.getAll<ICourseDto>(
          "course",
          ApiSuffixType.NoSuffix,
        );

        if (isCancelled) {
          return;
        }

        const requestedIds = new Set(uniqueCourseIds);
        const next: Record<number, ICourseDto> = {};
        for (const course of courses ?? []) {
          if (course?.id && requestedIds.has(course.id)) {
            next[course.id] = course;
          }
        }
        setCoursesById(next);
      } catch (error) {
        Log.debug("Course schedules preview: failed to load courses", error);
        if (!isCancelled) {
          // Keep drawer usable — ids still show without names
          setCoursesById({});
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingCourses(false);
        }
      }
    };

    void loadCourses();

    return () => {
      isCancelled = true;
    };
    // courseIdsKey keeps the effect stable when the same ids are recomputed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIdsKey]);

  return { coursesById, isLoadingCourses };
};
